// ReHub HDR Engine — admin dataset dashboard service (Phase 2).
// Wraps the SECURITY DEFINER RPCs added in
// supabase/migrations/20261013090000_hdr_engine_training_datasets.sql.

import { supabase, isConfigured, getSourceFileUrl } from './supabase';

// Public URL for a storage object in the `images` bucket (brackets + outputs
// live in the public bucket, so no signing is required for the admin viewer).
export const hdrStorageUrl = (storagePath: string | null | undefined): string | null => {
  if (!storagePath) return null;
  try {
    return getSourceFileUrl(storagePath);
  } catch {
    return null;
  }
};

export interface HdrProviderStat {
  total: number;
  valid: number;
  approved: number;
}

export interface HdrDatasetSummary {
  id: string;
  version: string;
  name: string | null;
  status: string;
  created_at?: string;
  members?: number;
}

export interface HdrActiveDataset {
  id: string;
  version: string;
  name: string | null;
  status: string;
  train: number;
  validation: number;
  test: number;
}

export interface HdrDatasetOverview {
  synced_at: string | null;
  inventory: {
    total_jobs: number;
    complete_brackets: number;
    with_output: number;
    missing_or_invalid: number;
    available_pairs: number;
    valid_pending_rights: number;
    pending_pixel_validation: number;
  };
  by_provider: Record<string, HdrProviderStat>;
  validation: Record<string, number>;
  quality_distribution: Record<string, number>;
  active_dataset: HdrActiveDataset | null;
  datasets: HdrDatasetSummary[];
}

export interface HdrTrainingPair {
  id: string;
  media_item_id: string;
  listing_id: string | null;
  listing_name: string | null;
  provider: string;
  media_type: string | null;
  source_count: number;
  source_width: number | null;
  source_height: number | null;
  output_width: number | null;
  output_height: number | null;
  camera_model: string | null;
  captured_at: string | null;
  processed_at: string | null;
  validation_status: string;
  meta_reasons: string[];
  pixel_reasons: string[];
  rights_status: string;
  quality: Record<string, unknown>;
  output_storage_path: string | null;
}

export const getHdrDatasetOverview = async (): Promise<{
  success: boolean;
  overview?: HdrDatasetOverview;
  error?: string;
}> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_hdr_dataset_overview');
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return { success: true, overview: data as HdrDatasetOverview };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

export const syncHdrTrainingPairs = async (): Promise<{
  success: boolean;
  upserted?: number;
  error?: string;
}> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_hdr_sync_training_pairs');
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return { success: true, upserted: data.upserted ?? 0 };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

export const listHdrTrainingPairs = async (opts: {
  limit?: number;
  offset?: number;
  provider?: string | null;
  validation?: string | null;
  rights?: string | null;
} = {}): Promise<{
  success: boolean;
  pairs?: HdrTrainingPair[];
  total?: number;
  error?: string;
}> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_hdr_list_training_pairs', {
      p_limit: opts.limit ?? 50,
      p_offset: opts.offset ?? 0,
      p_provider: opts.provider || null,
      p_validation: opts.validation || null,
      p_rights: opts.rights || null,
    });
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return { success: true, pairs: (data.pairs || []) as HdrTrainingPair[], total: data.total ?? 0 };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

export const setHdrPairRights = async (args: {
  rights: 'needs_review' | 'approved' | 'restricted';
  pairId?: string | null;
  provider?: string | null;
  notes?: string | null;
}): Promise<{ success: boolean; updated?: number; error?: string }> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_hdr_set_pair_rights', {
      p_rights: args.rights,
      p_pair_id: args.pairId || null,
      p_provider: args.provider || null,
      p_notes: args.notes || null,
    });
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return { success: true, updated: data.updated ?? 0 };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

// ── Phase 6/7/8: engine run history, job queue, pair detail ──────────────────

export interface HdrEngineRunRow {
  id: string;
  media_item_id: string | null;
  listing_id: string | null;
  final_action: 'deliver_internal' | 'deliver_fallback' | 'manual_review';
  delivered_source: string | null;
  provider_routed: string | null;
  providers_called: string[];
  attempts: number;
  retry_count: number;
  est_cost_usd: number;
  duration_ms: number | null;
  manual_review: boolean;
  model_versions: Record<string, string | null> | null;
  created_at: string;
}

export interface HdrEngineRunsOverview {
  totals: {
    runs: number;
    delivered_internal: number;
    delivered_fallback: number;
    manual_review: number;
    avg_cost_usd: number;
    total_cost_usd: number;
    avg_duration_ms: number;
    total_retries: number;
    quality_graded: number;
    quality_pass: number;
  };
  by_action: Record<string, number>;
  by_delivered_source: Record<string, number>;
  model_versions_latest: Record<string, string | null> | null;
  recent: HdrEngineRunRow[];
}

export interface HdrEngineJobsOverview {
  by_status: Record<string, number>;
  oldest_queued_at: string | null;
  recent: Array<{
    id: string;
    media_item_id: string;
    listing_id: string | null;
    mode: string;
    status: string;
    priority: number;
    worker_id: string | null;
    attempts: number;
    max_attempts: number;
    run_id: string | null;
    error_message: string | null;
    created_at: string;
    started_at: string | null;
    completed_at: string | null;
  }>;
}

export interface HdrPairDetail {
  id: string;
  media_item_id: string;
  listing_id: string | null;
  listing_name: string | null;
  provider: string;
  media_type: string | null;
  source_count: number;
  source_storage_paths: string[];
  source_width: number | null;
  source_height: number | null;
  output_storage_path: string | null;
  output_width: number | null;
  output_height: number | null;
  camera_model: string | null;
  validation_status: string;
  rights_status: string;
  quality: Record<string, unknown>;
}

export const getHdrEngineRunsOverview = async (
  limit = 50,
): Promise<{ success: boolean; overview?: HdrEngineRunsOverview; error?: string }> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_hdr_engine_runs_overview', { p_limit: limit });
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return { success: true, overview: data as HdrEngineRunsOverview };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

export const getHdrEngineJobsOverview = async (
  limit = 50,
): Promise<{ success: boolean; overview?: HdrEngineJobsOverview; error?: string }> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_hdr_engine_jobs_overview', { p_limit: limit });
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return { success: true, overview: data as HdrEngineJobsOverview };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

export const getHdrPairDetail = async (
  pairId: string,
): Promise<{ success: boolean; pair?: HdrPairDetail; error?: string }> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_hdr_pair_detail', { p_pair_id: pairId });
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return { success: true, pair: data.pair as HdrPairDetail };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

// ── Phase 9: benchmarking + blind comparison ─────────────────────────────────

// A per-provider / per-difficulty / overall preference breakdown.
export interface HdrBenchmarkPreference {
  votes: number;
  rehub: number;
  reference: number;
  tie: number;
  skip?: number;
  rehub_win_rate: number | null;
}

export interface HdrBenchmarkAggregate {
  count?: number;
  psnr_mean?: number | null;
  ssim_mean?: number | null;
  architectural_mean?: number | null;
  quality_score_mean?: number | null;
  quality_pass_rate?: number;
  fallback_frequency?: number;
  internal_acceptance_rate?: number;
  speed_ms_mean?: number | null;
  cpu_ms_mean?: number | null;
  internal_cost_usd?: number;
  baseline_cost_usd?: number;
  estimated_savings_usd?: number;
}

export interface HdrBenchmarkRun {
  id: string;
  label: string;
  manifest?: string | null;
  split: string | null;
  item_count: number;
  difficult_count: number;
  model_versions?: Record<string, string | null>;
  overall?: HdrBenchmarkAggregate; // present in the lightweight `runs` list
  metrics?: {
    overall?: HdrBenchmarkAggregate;
    difficult_scenes?: HdrBenchmarkAggregate & { by_tag?: Record<string, number> };
    by_provider?: Record<string, HdrBenchmarkAggregate>;
    gpu?: { available: boolean; device: string; count: number };
    peak_rss_mb?: number | null;
  };
  created_at: string;
}

export interface HdrBenchmarkOverview {
  latest: HdrBenchmarkRun | null;
  runs: HdrBenchmarkRun[];
}

export interface HdrBenchmarkNext {
  done: boolean;
  run_id: string | null;
  remaining: number;
  comparison_id?: string;
  is_difficult?: boolean;
  scene_tags?: string[];
  a_is_rehub?: boolean;
  slot_a?: string;
  slot_b?: string;
}

export interface HdrBenchmarkResults {
  run_id: string | null;
  total_comparisons?: number;
  overall: HdrBenchmarkPreference;
  by_provider?: Record<string, HdrBenchmarkPreference>;
  by_difficulty?: Record<string, HdrBenchmarkPreference>;
}

export const getHdrBenchmarkOverview = async (
  limit = 20,
): Promise<{ success: boolean; overview?: HdrBenchmarkOverview; error?: string }> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_hdr_benchmark_overview', { p_limit: limit });
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return { success: true, overview: data as HdrBenchmarkOverview };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

export const getHdrBenchmarkNext = async (
  runId: string | null = null,
): Promise<{ success: boolean; next?: HdrBenchmarkNext; error?: string }> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_hdr_benchmark_next', { p_run_id: runId });
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return { success: true, next: data as HdrBenchmarkNext };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

export const submitHdrBenchmarkVote = async (args: {
  comparisonId: string;
  slot: 'a' | 'b' | 'tie' | 'skip';
  aIsRehub: boolean;
}): Promise<{ success: boolean; choice?: string; error?: string }> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_hdr_benchmark_vote', {
      p_comparison_id: args.comparisonId,
      p_slot: args.slot,
      p_a_is_rehub: args.aIsRehub,
    });
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return { success: true, choice: data.choice };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

export const getHdrBenchmarkResults = async (
  runId: string | null = null,
): Promise<{ success: boolean; results?: HdrBenchmarkResults; error?: string }> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_hdr_benchmark_results', { p_run_id: runId });
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return { success: true, results: data as HdrBenchmarkResults };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

// ── Sandbox: run the engine on one media item from the dashboard ─────────────

export interface HdrSandboxJobDetail {
  job: {
    id: string;
    media_item_id: string;
    listing_id: string | null;
    pair_id: string | null;
    mode: string;
    status: 'queued' | 'processing' | 'completed' | 'failed';
    worker_id: string | null;
    attempts: number;
    max_attempts: number;
    error_message: string | null;
    result_image_path: string | null;
    result_image_gemini_path: string | null;
    result_image_openai_path: string | null;
    result_image_fal_path: string | null;
    result_image_candidate_path: string | null;
    result_candidate_meta: Record<string, any> | null;
    /** Every finish profile on this merge (path, quality, checks) plus the baseline's checks. */
    result_candidates?: Record<string, HdrFinishCandidate> | null;
    /** Per provider: which ReHub render it was given and the sky guard's decision. */
    sandbox_provider_meta?: Record<string, HdrProviderMeta> | null;
    params?: Record<string, any> | null;
    candidate_preference: string | null;
    sandbox_bracket_paths: string[] | null;
    run_id: string | null;
    created_at: string;
    started_at: string | null;
    completed_at: string | null;
  };
  run: {
    final_action: 'deliver_internal' | 'deliver_fallback' | 'manual_review';
    delivered_source: string | null;
    provider_routed: string | null;
    providers_called: string[];
    attempts: number;
    retry_count: number;
    est_cost_usd: number;
    duration_ms: number | null;
    manual_review: boolean;
    notes: string | null;
    quality: Record<string, any> | null;
    model_versions: Record<string, string | null> | null;
  } | null;
  source: {
    provider: string;
    listing_name: string | null;
    source_storage_paths: string[];
    source_count: number;
    output_storage_path: string | null;
    output_width: number | null;
    output_height: number | null;
  } | null;
}

export interface HdrFinishChecks {
  profile: string;
  wall_l: number | null;
  neutral_cast_ab: number | null;
  neutral_patch_ab: number | null;
  clipped_fraction: number;
  texture_vs_merge: number | null;
  window_hf_vs_merge?: number | null;
  window_darker_fraction?: number | null;
  flags: string[];
}

export interface HdrFinishCandidate {
  path?: string;
  profile?: string;
  quality?: Record<string, any> | null;
  checks?: HdrFinishChecks | null;
  finish_ms?: number;
}

export interface HdrSkyGuard {
  decision: 'applied' | 'skipped';
  reason: string;
  path?: string;
  sky_fraction?: number;
  provider_sky_outside_mask?: number;
  unconfirmed_share?: number;
}

export interface HdrProviderMeta {
  input_profile: string | null;
  sky_prompt: boolean;
  sky_guard?: HdrSkyGuard;
}

export type HdrPreferenceChoice =
  | 'baseline' | 'bright' | 'bright-2' | 'historical' | 'gemini' | 'openai' | 'fal' | 'tie';

export const enqueueHdrSandbox = async (args: {
  mediaItemId: string;
  modelDir?: string | null;
  providerOrder?: string[] | null;
  maxAttempts?: number | null;
  gemini?: boolean;
  geminiSky?: boolean;
  openai?: boolean;
  openaiSky?: boolean;
  fal?: boolean;
  falSky?: boolean;
  falModel?: string | null;
  /** ReHub render the providers are given: the baseline (default) or a finish profile. */
  providerBase?: 'baseline' | 'bright-2' | null;
}): Promise<{ success: boolean; jobId?: string; error?: string }> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  const params: Record<string, unknown> = {};
  if (args.modelDir) params.model_dir = args.modelDir;
  if (args.providerOrder && args.providerOrder.length) params.provider_order = args.providerOrder;
  if (args.maxAttempts) params.max_attempts = args.maxAttempts;
  if (args.gemini) params.gemini = true;
  if (args.gemini && args.geminiSky) params.gemini_sky = true;
  if (args.openai) params.openai = true;
  if (args.openai && args.openaiSky) params.openai_sky = true;
  if (args.fal) params.fal = true;
  if (args.fal && args.falSky) params.fal_sky = true;
  if (args.fal && args.falModel) params.fal_model = args.falModel;
  if (args.providerBase && args.providerBase !== 'baseline') params.provider_base = args.providerBase;
  try {
    const { data, error } = await supabase.rpc('admin_hdr_engine_sandbox_enqueue', {
      p_media_item_id: args.mediaItemId,
      p_params: params,
    });
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return { success: true, jobId: data.job_id as string };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

export const preferHdrSandboxCandidate = async (
  jobId: string,
  choice: HdrPreferenceChoice,
  shown: string[] = [],
  blind = false,
): Promise<{ success: boolean; error?: string }> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_hdr_engine_sandbox_prefer', {
      p_job_id: jobId,
      p_choice: choice,
      p_shown: shown,
      p_blind: blind,
    });
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return { success: true };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

export const getHdrEngineJobDetail = async (
  jobId: string,
): Promise<{ success: boolean; detail?: HdrSandboxJobDetail; error?: string }> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_hdr_engine_job_detail', { p_job_id: jobId });
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return { success: true, detail: data as HdrSandboxJobDetail };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

// ── Phase 10: deployment & rollout control plane ─────────────────────────────

export type HdrRolloutMode = 'external_only' | 'shadow' | 'hybrid' | 'internal_testing';

export interface HdrRolloutConfig {
  mode: HdrRolloutMode;
  enabled: boolean;
  canary_percent: number;
  fallback_provider: string;
  approved_by: string | null;
  approved_at: string | null;
  approval_note: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

export interface HdrRolloutEvent {
  id: string;
  event: 'set' | 'rollback';
  from_mode: string | null;
  to_mode: string | null;
  enabled: boolean | null;
  canary_percent: number | null;
  actor: string | null;
  note: string | null;
  created_at: string;
}

export interface HdrRolloutState {
  config: HdrRolloutConfig;
  live_provider: string;
  events: HdrRolloutEvent[];
}

export const getHdrRollout = async (): Promise<{
  success: boolean; state?: HdrRolloutState; error?: string;
}> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_hdr_rollout_get');
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return { success: true, state: data as HdrRolloutState };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

export const setHdrRollout = async (args: {
  mode: HdrRolloutMode;
  enabled: boolean;
  canaryPercent?: number;
  note?: string | null;
}): Promise<{ success: boolean; state?: HdrRolloutState; error?: string }> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_hdr_rollout_set', {
      p_mode: args.mode,
      p_enabled: args.enabled,
      p_canary_percent: args.canaryPercent ?? 0,
      p_note: args.note ?? null,
    });
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return { success: true, state: data as HdrRolloutState };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

export const rollbackHdrRollout = async (
  reason?: string,
): Promise<{ success: boolean; state?: HdrRolloutState; error?: string }> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_hdr_rollout_rollback', {
      p_reason: reason ?? null,
    });
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return { success: true, state: data as HdrRolloutState };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};
